// Őrszem (2.5/2.6, UI-7): what the receiving side said about us, what the
// watchdog did about it, and who let a pool out again. During a warmup the
// questions are short — which pool is held and why, which rule moved it, is
// every source still answering, and is the last hour normal for this hour —
// and the one action is a release, which names a person and a reason.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { RefreshCwIcon } from "lucide-react";
import { fetchWatchPools } from "@/lib/api";
import type { PoolWatchRow, WarmupPool } from "@/lib/api";
import { fetchAnomalyCells, fetchTimeline, fetchWatchSources } from "@/lib/tracking";
import type { TimelineEntry, WatchSource } from "@/lib/tracking";
import { useAuth } from "@/lib/auth";
import { useLoad } from "@/lib/useLoad";
import type { Loaded } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable } from "@/components/emc/DataTable";
import { PageHeader } from "@/components/emc/PageHeader";
import { PoolLink } from "@/components/emc/PoolLink";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import type { Tone } from "@/components/emc/status";
import { TONE_FILL } from "@/components/emc/tone";
import {
  METRIC_ORDER,
  SIGNAL_SOURCES,
  anomalyGrid,
  budget,
  dayGroups,
  describe,
  sourceRows,
} from "@/pages/watchdog/model";
import type { GridCell, GridPool } from "@/pages/watchdog/model";
import { canRelease, heldByWarmupOnly, releaseCard, releaseHeldPool } from "@/pages/watchdog/release";
import { releaseCopy } from "@/pages/warmup/model";
import { cn } from "@/lib/utils";

const REFRESH_MS = 120_000;
const DAY_CHOICES = [1, 7, 14, 30];
// ReleaseIn: the API keeps 300 characters of a note.
const NOTE_MAX = 300;

const TONE_TEXT: Record<Tone, string> = {
  running: "text-blue-ink",
  scheduled: "text-indigo-ink",
  good: "text-green-ink",
  warn: "text-orange-ink",
  danger: "text-red-ink",
  muted: "text-foreground",
};

// --- the pools now ---------------------------------------------------------------

function poolColumns({
  isAdmin,
  onRelease,
  onLog,
  logFor,
}: {
  isAdmin: boolean;
  onRelease: (row: PoolWatchRow) => void;
  onLog: (pool: string) => void;
  logFor: string;
}): ColumnDef<PoolWatchRow>[] {
  return [
    { id: "pool", header: "Pool", cell: ({ row }) => <PoolLink pool={row.original.pool} /> },
    {
      id: "state",
      header: "Őrszem",
      cell: ({ row }) => <StatusPill domain="poolWatch" state={row.original.state} />,
    },
    {
      id: "since",
      header: "Mióta",
      cell: ({ row }) => minute(row.original.since),
      meta: { mono: true, muted: true },
    },
    {
      id: "reason",
      header: "Miért",
      cell: ({ row }) =>
        heldByWarmupOnly(row.original) ? (
          <span>
            {row.original.paused_reason || "a warmup megállította"}
            <span className="block text-label text-muted-foreground">
              A warmup állította meg a napi számok miatt; az őrszem a következő ciklusában (legfeljebb
              egy órán belül) veszi át. Feloldható innen is; a számok a{" "}
              <Link to={`/warmup/${encodeURIComponent(row.original.pool)}`}>Warmup</Link> lapon.
            </span>
          </span>
        ) : row.original.state === "watching" ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span>
            {row.original.reason || row.original.paused_reason || "—"}
            {row.original.actor && row.original.actor !== "watchdog" && (
              <span className="text-muted-foreground"> · {row.original.actor}</span>
            )}
          </span>
        ),
      meta: { wrap: true },
    },
    {
      id: "budget",
      header: "Napi keret",
      cell: ({ row }) => {
        const { now, full, throttled, idle } = budget(row.original);
        if (idle !== null) {
          // A limit on a pool that sends nothing today is not a budget.
          return (
            <span className="text-muted-foreground" title={`${idle}; a beállított limit ${count(full)}`}>
              —
            </span>
          );
        }
        return throttled ? (
          <span title={`a teljes keret ${count(full)}`}>
            <span className="font-medium text-warn">{count(now)}</span>
            <span className="text-muted-foreground"> / {count(full)}</span>
          </span>
        ) : (
          count(full)
        );
      },
      meta: { align: "right" },
    },
    {
      id: "warmup",
      header: "Warmup",
      cell: ({ row }) => <StatusPill domain="warmup" state={row.original.warmup_state} />,
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button
            variant={logFor === row.original.pool ? "secondary" : "outline"}
            size="sm"
            type="button"
            onClick={() => onLog(row.original.pool)}
          >
            Napló
          </Button>
          {isAdmin && canRelease(row.original) && (
            <Button size="sm" type="button" onClick={() => onRelease(row.original)}>
              Feloldás
            </Button>
          )}
        </div>
      ),
      meta: { align: "right" },
    },
  ];
}

// --- the sources -------------------------------------------------------------------

function SourcesStrip({ runs, now }: { runs: Loaded<WatchSource[]>; now: Date }) {
  if (runs.error !== null) return <ErrorState message={runs.error} />;
  if (runs.data === null) return <LoadingState lines={1} />;
  const rows = sourceRows(runs.data, now);
  return (
    <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(var(--emc-tile-min),1fr))] gap-3 p-0">
      {rows.map((row) => (
        <li
          key={row.source}
          className="flex min-w-0 flex-col gap-1 rounded-card border border-line bg-surface px-3 py-2.5"
        >
          <span className="caps-label truncate">{row.label}</span>
          <StatusPill domain="sourceRun" state={row.state} className="w-fit" />
          <span className="data-mono text-label text-muted-foreground">
            {row.run ? minute(row.run.last_run_at) : "egyszer sem futott"}
          </span>
        </li>
      ))}
    </ul>
  );
}

// --- the timeline -------------------------------------------------------------------

function TimelineItem({ entry }: { entry: TimelineEntry }) {
  const said = describe(entry);
  return (
    <li className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-4 border-b border-line py-3 last:border-b-0">
      <span className="data-mono pt-0.5 text-muted-foreground">
        {new Date(entry.occurred_at).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" })}
      </span>
      <div className={cn("flex min-w-0 flex-col gap-1", said.muted && "text-muted-foreground")}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {entry.pool && <PoolLink pool={entry.pool} />}
          {said.pill && !said.muted && <StatusPill domain={said.pill.domain} state={said.pill.state} />}
          {entry.kind === "signal" && <span className="text-label text-muted-foreground">hallotta</span>}
          {entry.injected && <StatusPill tone="muted">próba (injektált)</StatusPill>}
          <span className="min-w-0 font-medium">{said.title}</span>
        </div>
        {said.quote && <blockquote className="m-0 border-l-2 border-line-strong pl-3">„{said.quote}”</blockquote>}
        {said.detail && <p className="m-0 text-label text-muted-foreground">{said.detail}</p>}
      </div>
    </li>
  );
}

// --- the 2.6 grid --------------------------------------------------------------------

function Cell({ cell }: { cell: GridCell | null }) {
  if (cell === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-col items-end gap-0.5" title={cell.title}>
      <span className={cn("font-medium tabular-nums", TONE_TEXT[cell.tone])}>
        {cell.tone !== "muted" && (
          <span
            aria-hidden="true"
            className={cn("mr-1.5 inline-block size-1.5 rounded-full align-middle", TONE_FILL[cell.tone])}
          />
        )}
        {cell.observed}
      </span>
      <span className="text-label text-muted-foreground">
        várt {cell.expected} · {cell.deviation}
      </span>
      {cell.tone !== "muted" && <span className={cn("text-label", TONE_TEXT[cell.tone])}>{cell.verdict}</span>}
    </span>
  );
}

function AnomalyPool({ grid }: { grid: GridPool }) {
  const rows = grid.isps.map((isp) => ({ isp, cells: grid.cells[isp] ?? {} }));
  const columns: ColumnDef<(typeof rows)[number]>[] = [
    { id: "isp", header: "Levelező", cell: ({ row }) => labelOf("isp", row.original.isp) },
    ...METRIC_ORDER.map(
      (metric): ColumnDef<(typeof rows)[number]> => ({
        id: metric,
        header: labelOf("anomalyMetric", metric),
        cell: ({ row }) => <Cell cell={row.original.cells[metric] ?? null} />,
        meta: { align: "right" },
      }),
    ),
  ];
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h3 className="flex items-center gap-2">
        <PoolLink pool={grid.pool} />
        <span className="text-label font-normal text-muted-foreground">— {grid.summary}</span>
      </h3>
      <DataTable
        label={`Eltérések, ${grid.pool}`}
        variant="plain"
        className="-mx-5.5 border-t border-separator"
        columns={columns}
        data={rows}
        getRowId={(row) => row.isp}
      />
    </section>
  );
}

// --- the page -------------------------------------------------------------------------

export default function Watchdog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const pool = (params.get("pool") ?? "").toUpperCase();
  const source = params.get("source") ?? "";
  const days = Number(params.get("days") ?? "7") || 7;
  const [releasing, setReleasing] = useState<PoolWatchRow | null>(null);
  // The pool's warmup card, read when the dialog opens: the release speaks
  // the Warmup page's words, its re-pause warning included.
  const [card, setCard] = useState<WarmupPool | null>(null);
  const [now, setNow] = useState(() => new Date());

  const [pools, reloadPools] = useLoad(fetchWatchPools, []);
  const [timeline, reloadTimeline] = useLoad(
    () => fetchTimeline({ pool: pool || undefined, source: source || undefined, days, limit: 300 }),
    [pool, source, days],
  );
  const [cells, reloadCells] = useLoad(() => fetchAnomalyCells(), []);
  const [runs, reloadRuns] = useLoad(fetchWatchSources, []);

  const refresh = useCallback(() => {
    setNow(new Date());
    reloadPools();
    reloadTimeline();
    reloadCells();
    reloadRuns();
  }, [reloadPools, reloadTimeline, reloadCells, reloadRuns]);

  useEffect(() => {
    const timer = window.setInterval(refresh, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  useEffect(() => {
    setCard(null);
    if (releasing === null) return;
    let live = true;
    releaseCard(releasing.pool)
      .then((read) => live && setCard(read))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [releasing]);

  const held = (pools.data ?? []).filter((row) => row.state === "throttled" || row.state === "paused");
  const warmupHeld = (pools.data ?? []).filter(heldByWarmupOnly);
  const copy = card !== null && card.code === releasing?.pool ? releaseCopy(card) : null;
  const groups = useMemo(() => dayGroups(timeline.data ?? []), [timeline.data]);
  const grid = useMemo(() => anomalyGrid(cells.data ?? []), [cells.data]);
  const hour = cells.data?.[0]?.hour ?? null;

  return (
    <>
      <PageHeader
        title="Őrszem"
        description={
          <>
            Amit a fogadó oldal mond rólunk — Google Postmaster, Microsoft SNDS, DMARC-riportok,
            a SES saját ítélete, a feketelisták és a saját adatunk eltérései —, és amit az őrszem
            tett vele. Egy „baj” jel megállítja a poolt, és csak ember engedi tovább; egy
            figyelmeztetés felezi a napi keretet, és a forrás megnyugvása után magától visszaáll.
          </>
        }
        actions={
          <Button variant="outline" type="button" onClick={refresh}>
            <RefreshCwIcon />
            Frissítés
          </Button>
        }
      />

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Poolok most</CardTitle>
            <CardDescription>
              Az őrszem állapota poolonként. A feloldás két lépés a naplóban — ki és miért,
              majd hogy újra figyeljük —, és a warmup ott folytatja, ahol megállt.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {pools.error !== null && <ErrorState message={pools.error} onRetry={reloadPools} />}
            {pools.data !== null && held.length === 0 && warmupHeld.length === 0 && pools.data.length > 0 && (
              <Callout tone="success">Egyetlen poolt sem tart vissza az őrszem.</Callout>
            )}
            {pools.data !== null && held.length === 0 && warmupHeld.length > 0 && (
              <Callout tone="warn">
                Az őrszem egyet sem tart vissza, de a warmup a napi számok miatt megállította:{" "}
                {warmupHeld.map((row) => row.pool).join(", ")}.
              </Callout>
            )}
            <DataTable
              label="Poolok most"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={poolColumns({
                isAdmin,
                onRelease: setReleasing,
                onLog: (code) => setFilter("pool", pool === code ? "" : code),
                logFor: pool,
              })}
              data={pools.data ?? []}
              loading={pools.data === null && pools.error === null}
              getRowId={(row) => row.pool}
              isRowSelected={(row) => row.pool === pool}
              empty={<EmptyState bare title="Nincs egyetlen IP-pool sem felvéve." />}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Források</CardTitle>
            <CardDescription>
              Mikor kérdezte meg utoljára az őrszem az egyes forrásokat. Ami „nincs bekötve”, arról
              nem jöhet jel — kulcs vagy hozzáférés kell hozzá (KERDESEK 1.6); ami „késik”, arról
              az őrszem ciklusa rég nem kérdezett.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SourcesStrip runs={runs} now={now} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Jelek és döntések</CardTitle>
            <CardDescription>
              Minden mozdulat a jellel, ami kiváltotta, és a döntési tábla sorával, amin átment;
              a feloldás azzal, aki tette, és az indokával. Közöttük a figyelmeztetések, amelyeket
              az őrszem hallott, de poolt nem mozdítottak.
            </CardDescription>
            <CardAction>
              <span className="text-label text-muted-foreground">
                {timeline.data ? `${timeline.data.length} tétel` : ""}
              </span>
            </CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Toolbar>
              <NativeSelect aria-label="Pool" value={pool} onChange={(event) => setFilter("pool", event.target.value)}>
                <option value="">Minden pool</option>
                {(pools.data ?? []).map((row) => (
                  <option key={row.pool} value={row.pool}>
                    {row.pool}
                  </option>
                ))}
              </NativeSelect>
              <NativeSelect
                aria-label="Forrás"
                value={source}
                onChange={(event) => setFilter("source", event.target.value)}
              >
                <option value="">Minden forrás</option>
                {SIGNAL_SOURCES.map((name) => (
                  <option key={name} value={name}>
                    {labelOf("watchSource", name)}
                  </option>
                ))}
              </NativeSelect>
              <NativeSelect
                aria-label="Időszak"
                value={String(days)}
                onChange={(event) => setFilter("days", event.target.value === "7" ? "" : event.target.value)}
              >
                {DAY_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice === 1 ? "utolsó nap" : `utolsó ${choice} nap`}
                  </option>
                ))}
              </NativeSelect>
            </Toolbar>
            {timeline.error !== null && <ErrorState message={timeline.error} onRetry={reloadTimeline} />}
            {timeline.data === null ? (
              timeline.error === null && <LoadingState lines={4} />
            ) : timeline.data.length === 0 ? (
              <EmptyState
                bare
                title="Ebben az időszakban semmi sem történt."
                description="Se mozdulat, se figyelmeztetés. Ha a források „nincs bekötve” állapotúak, ez nem biztosíték arra, hogy minden rendben van."
              />
            ) : (
              <div className="flex flex-col gap-4">
                {groups.map((group) => (
                  <section key={group.key} className="flex flex-col">
                    <h3 className="caps-label border-b border-line pb-2">{group.label}</h3>
                    <ul className="m-0 flex list-none flex-col p-0">
                      {group.entries.map((entry, index) => (
                        <TimelineItem key={`${entry.occurred_at}-${entry.kind}-${index}`} entry={entry} />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Eltérések — a legutóbbi lezárt óra</CardTitle>
            <CardDescription>
              A saját adatunk (2.6): poolonként és levelezőnként amit mértünk, amit az előző hét
              ugyanezen órája alapján várni lehetett, a kettő aránya, és amit a szabályok ebből
              mondtak. A cellára állva látszik, melyik szabály mit nézett és milyen küszöbbel.
              {hour ? ` Az óra: ${minute(hour)}.` : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {cells.error !== null && <ErrorState message={cells.error} onRetry={reloadCells} />}
            {cells.data === null ? (
              cells.error === null && <LoadingState lines={3} />
            ) : grid.length === 0 ? (
              <p className="text-muted-foreground">
                Ebben az órában egyetlen pool sem küldött. Ez nem nulla kézbesítés, hanem nulla
                küldés.
              </p>
            ) : (
              grid.map((item) => <AnomalyPool key={item.pool} grid={item} />)
            )}
            <p className="text-label text-muted-foreground">
              A nyitás és a kattintás az eseménytárházból jön; ha az nem válaszol, a helyükön „—”
              áll. A Kézbesítés napi számai a <Link to="/kezbesites">Kézbesítés</Link> oldalon.
            </p>
          </CardContent>
        </Card>
      </div>

      <ActionDialog
        open={releasing !== null}
        onOpenChange={(open) => !open && setReleasing(null)}
        title={releasing ? `${releasing.pool} pool feloldása` : "Feloldás"}
        description={
          copy !== null
            ? copy.description
            : releasing?.state === "throttled"
              ? "A napi keret visszaáll a teljesre, és a pool újra a jelek szerint mozog. A naplóba a neved és az indok kerül, a riasztás is ezt mondja."
              : "A pool újra küldhet a warmup keretén belül, és ott folytatja, ahol megállt. A naplóba a neved és az indok kerül, a riasztás is ezt mondja."
        }
        confirmLabel="Feloldás"
        reason={{
          label: "Miért oldod fel?",
          placeholder: "pl. az SNDS újra zöld, a Microsoft-forgalom rendben",
          max: NOTE_MAX,
        }}
        onConfirm={async (note) => {
          if (releasing === null) return;
          await releaseHeldPool(releasing.pool, note);
          refresh();
        }}
      >
        {releasing && (
          <p className="m-0 text-muted-foreground">
            Most: <StatusPill domain="poolWatch" state={releasing.state} /> —{" "}
            {releasing.reason || releasing.paused_reason || "ok nélkül"}
            {releasing.since ? ` (${minute(releasing.since)} óta)` : ""}.{" "}
            {releasing.state === "paused" && "Megállított poolból nincs automatikus kiút: ez a lépés az."}
          </p>
        )}
        {copy?.warning && <Callout tone="warn">{copy.warning}</Callout>}
      </ActionDialog>
    </>
  );
}
